import { usePaywallCustomization } from "@circle-react/contexts/Paywalls/usePaywallCustomization";
import { createContext, useContext, useMemo } from "react";
import type { PropsWithChildren } from "react";
import invariant from "tiny-invariant";

import type { PaywallCustomization } from "@/react/types/PaywallCustomization";

import type { CheckoutPreviewContext } from "../../../types/CheckoutProps";

interface PaywallCustomizationContext {
  checkoutPreviewContext?: CheckoutPreviewContext;
  paywallCustomization?: PaywallCustomization;
}

const PaywallCustomizationProvider =
  createContext<PaywallCustomizationContext | null>(null);

PaywallCustomizationProvider.displayName = "PaywallCustomizationProvider";

export const usePaywallCustomizationContext = () => {
  const context = useContext(PaywallCustomizationProvider);

  invariant(
    context,
    "usePaywallCustomizationContext should be within the PaywallCustomizationContext",
  );

  return context;
};

export const PaywallCustomizationContext = ({
  children,
  paywallCustomization,
  checkoutPreviewContext,
}: PropsWithChildren<PaywallCustomizationContext>) => {
  const paywallCustomizationSerialized =
    usePaywallCustomization(paywallCustomization);

  const values: PaywallCustomizationContext = useMemo(
    () => ({
      paywallCustomization: paywallCustomizationSerialized,
      checkoutPreviewContext,
    }),
    [
      paywallCustomization,
      checkoutPreviewContext,
      paywallCustomizationSerialized,
    ],
  );

  return (
    <PaywallCustomizationProvider.Provider value={values}>
      {children}
    </PaywallCustomizationProvider.Provider>
  );
};
